import { Injectable } from '@angular/core';

/**
 * Persists KDS line "done" (crossed-out) state across API refreshes and polling.
 * Keys: `${orderId}:${orderDetailId}` (detail id from API when present; falls back to index).
 */
@Injectable({
  providedIn: 'root'
})
export class KdsLineDoneStateService {
  private readonly doneLineKeys = new Set<string>();

  lineKey(orderId: number | string, line: { id?: number } | null, lineIndex: number): string {
    const lid = line?.id != null ? String(line.id) : `idx:${lineIndex}`;
    return `${orderId}:${lid}`;
  }

  isDone(orderId: number | string, line: { id?: number } | null, lineIndex: number): boolean {
    return this.doneLineKeys.has(this.lineKey(orderId, line, lineIndex));
  }

  setDone(orderId: number | string, line: { id?: number } | null, lineIndex: number, done: boolean): void {
    const key = this.lineKey(orderId, line, lineIndex);
    if (done) {
      this.doneLineKeys.add(key);
    } else {
      this.doneLineKeys.delete(key);
    }
  }

  /** Remove all line keys for an order (e.g. after Mark Done or order leaves KDS). */
  clearOrder(orderId: number | string): void {
    const prefix = `${orderId}:`;
    for (const k of this.doneLineKeys) {
      if (k.startsWith(prefix)) {
        this.doneLineKeys.delete(k);
      }
    }
  }

  /** Apply stored flags to order line items (mutates order detail models). */
  applyDoneFlagsToOrders(orders: any[] | null): any[] | null {
    if (!orders?.length) {
      return orders;
    }
    for (const order of orders) {
      const lines = order?.orderDetailsModelList;
      if (!Array.isArray(lines)) {
        continue;
      }
      lines.forEach((line: any, idx: number) => {
        if (line?.kdsDone === true || line?.done === true || this.isDone(order.id, line, idx)) {
          line.done = true;
        }
      });
    }
    return orders;
  }
}
